<template>
  <!-- Ignore div when component is rendered inside css grid layout (tile view) -->
  <div :class="{ 'd-contents': selectedView === views.Tile }" class="sidebar-wrapper">
    <!-- div at top to span the amount of rows in the headers, allows for overflow scrolling -->
    <div
      :style="sidebarHeadingStyle"
      class="grid-item-border left-column-sticky-grid-item top-left-heading d-flex flex-column justify-space-between"
      :class="isSticky"
    >
      <div class="controls-container">
        <div>
          <v-menu v-model="isViewMenuOpen" close-on-content-click location="end top">
            <template #activator="{ props }">
              <v-btn v-bind="props" class="mb-3 d-flex align-center view-options-btn" primary>
                <v-icon color="white" size="small" class="mr-1">mdi-eye</v-icon>
                {{ tkey('viewOptions') }}
              </v-btn>
            </template>

            <v-card class="ml-1">
              <v-list class="pb-0">
                <v-list-item>
                  <template #prepend>
                    <v-switch
                      :model-value="fullScreenExpanded"
                      small
                      density="compact"
                      color="primary"
                      class="view-menu-switch pr-4"
                      hide-details
                      @update:model-value="toggleFullScreen(!fullScreenExpanded)"
                    />
                  </template>
                  <v-list-item-title class="view-menu-option">
                    {{ tkey('fullscreen') }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item>
                  <template #prepend>
                    <v-switch
                      v-model="stickyHeadersModel"
                      small
                      density="compact"
                      color="primary"
                      class="view-menu-switch pr-4"
                      hide-details
                    />
                  </template>
                  <v-list-item-title class="view-menu-option">
                    {{ tkey('stickyHeaders') }}
                  </v-list-item-title>
                </v-list-item>

                <v-list-item>
                  <template #prepend>
                    <v-switch
                      v-model="unifiedColumnsModel"
                      small
                      density="compact"
                      color="primary"
                      class="view-menu-switch pr-4"
                      hide-details
                    />
                  </template>
                  <v-list-item-title class="view-menu-option">
                    {{ tkey('unifiedColumns') }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
              <v-card-actions class="view-menu-actions mr-2">
                <v-spacer />
                <v-btn color="primary" variant="text" @click="isViewMenuOpen = false">
                  {{ $t('actions.close') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </div>

        <!-- This column settings panel is only available on the List view -->
        <!-- It allows to select and reorder columns to display -->
        <div class="d-flex">
          <ColumnSettings
            v-if="selectedView === views.List && canvasListViewColumnSettingsEnabled"
            :button-title="tkey('columns')"
            :columns="listViewColumnsWithTitle"
            :disabled-columns="listViewFixedColumns"
            @update:selected-columns="toggleListViewColumnVisibility"
            @update:column-order="reorderListViewColumns"
          />
        </div>
      </div>

      <!-- Global Constraints -->
      <div class="spacebreak-entry global-constraints" :class="selectedSpacebreakClass">
        <div class="spacebreak-heading-row">
          <v-layout class="d-flex justify-space-between overflow-visible">
            <div class="spacebreak-name space-break-heading">
              {{ tkey('globalConstraints') }}
            </div>
            <div
              class="icons-container"
              data-dd-action-name="ToggleGlobalSpacebreakSettingsPanelBtn"
            >
              <v-avatar
                :class="{
                  'spacebreak-count-badge': !cogWarning,
                  'spacebreak-count-badge-warning': cogWarning,
                }"
                size="14"
                @click="toggleGlobalSettingsPanel"
              >
                <span class="text-white text-h6">{{ globalConstraintsCount || 0 }}</span>
              </v-avatar>
              <v-icon class="settings-btn" @click="toggleGlobalSettingsPanel"> $settings </v-icon>
            </div>
          </v-layout>
        </div>
      </div>
    </div>
    <!-- Spacebreaks with products -->
    <div
      v-for="spacebreak in spacebreaks"
      :key="spacebreak._id"
      :class="{
        'spacebreak-locked-border':
          isSpacebreakLocked(spacebreak._id) && selectedView === views.Tile,
      }"
      class="grid-item-border left-column-sticky-grid-item sidebar d-flex"
    >
      <div v-if="isSpacebreakLocked(spacebreak._id) && selectedView === views.Tile" class="locked">
        {{ tkey('locked') }}
      </div>
      <SpacebreakEntry
        :spacebreak="spacebreak"
        :space="spaceUsedPerSpacebreak[spacebreak._id]"
        :pallets-total="palletSlotsPerSpacebreak(spacebreak)"
        :used-pallets-slots="usedPalletsSlotsPerSpacebreak[spacebreak._id]"
        :store-total="pointsOfDistribution[spacebreak._id]"
        :product-count="productCountPerSpacebreak[spacebreak._id]"
        class="list-spacebreak-entry"
        :class="{
          'active-spacebreak': isSpacebreakActive(spacebreak._id),
        }"
        @update-active-spacebreak="updateActiveSpacebreak(spacebreak._id)"
      />
    </div>
    <!-- Entries for products with no assigned spacebreak -->
    <div
      v-for="spacebreak in spacebreakTypes"
      :id="`${spacebreak}-spacebreak-entry`"
      :key="spacebreak"
      class="pa-3 grid-item-border left-column-sticky-grid-item sidebar"
      :class="{
        'active-spacebreak': isSpacebreakActive(spacebreak),
        selectable: selectedView === views.List,
      }"
      @click="updateActiveSpacebreak(spacebreak)"
    >
      <p class="font-weight-bold pb-4">
        {{ tkey(spacebreak) }}
      </p>
      <p v-if="productsWithoutSpacebreak[spacebreak]">
        {{
          `${productsWithoutSpacebreak[spacebreak].length} ${$t(
            'assortmentCanvasPage.listViewPage.products',
            productsWithoutSpacebreak[spacebreak].length
          )}`
        }}
      </p>
    </div>
  </div>
</template>

<script>
import {
  cloneDeep,
  every,
  filter,
  get,
  isEmpty,
  map,
  reduce,
  set,
  size,
  sortBy,
  values,
} from 'lodash';
import { mapActions, mapState } from 'pinia';

// enums
import assortmentCanvases from '@enums/assortment-canvases';
import SpacebreakTypes from '@enums/spacebreak-types';

// composables
import { useFeature } from '@/composables/use-feature';
import { useInternationalization } from '@/composables/use-internationalization';

// stores
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useScenarioProductsStore } from '@/stores/scenario-products';

export default {
  props: {
    pointsOfDistribution: {
      type: Object,
      required: true,
    },
  },

  setup() {
    const { tkey } = useInternationalization('assortmentCanvasPage.listViewPage');
    const { canvasListViewColumnSettingsEnabled } = useFeature();

    return {
      tkey,
      canvasListViewColumnSettingsEnabled,
    };
  },

  data() {
    return {
      isViewMenuOpen: false,
      views: assortmentCanvases.Views,
      spacebreakTypes: SpacebreakTypes.spacebreakTypes,
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'spacebreakSettings',
      'fullScreenExpanded',
      'stickyHeaders',
      'unifiedColumns',
      'spacebreaks',
      'selectedView',
      'sidebarHeaderHeight',
      'activeSpacebreak',
      'productsPerSpacebreak',
      'totalExpandedHeaderDepth',
      'selectedCanvas',
      'spaceUsedPerSpacebreak',
      'isSpacebreakLocked',
      'getFilteredProducts',
      'getProductsWithNoSpacebreak',
      'listViewColumns',
      'listViewFixedColumns',
    ]),
    ...mapState(useScenarioProductsStore, ['scenarioProducts']),

    productCountPerSpacebreak() {
      const { productCounts } = reduce(
        sortBy(this.spacebreaks, 'fillOverride'),
        (acc, sb) => {
          // Ignore products in unassigned spacebreak
          if (!sb._id) return acc;
          const productsExcludingPallets = filter(
            this.productsPerSpacebreak[sb._id],
            ({ palletContent }) => isEmpty(palletContent)
          );
          const productCount = size(map(productsExcludingPallets, 'productKey'));
          const currentCount = productCount + acc.previousProductCount;
          acc.previousProductCount = currentCount;
          return set(acc, `productCounts.${sb._id}`, currentCount);
        },
        { productCounts: {}, previousProductCount: 0 }
      );
      return productCounts;
    },

    usedPalletsSlotsPerSpacebreak() {
      const productKeyDisplays = new Set(this.scenarioProducts.map(p => p.productKeyDisplay));
      const { usedSpacePerSpacebreaks } = reduce(
        sortBy(this.spacebreaks, 'fillOverride'),
        (acc, sb) => {
          if (!sb._id) return acc;
          const palletsProducts = filter(this.productsPerSpacebreak[sb._id], product => {
            if (productKeyDisplays.has(product.palletContent)) return product;
            return null;
          });

          let usedSpace = 0;
          palletsProducts.forEach(p => {
            const { palletContent, palletSize } = p;
            if (palletSize) {
              if (
                !acc.latestUsedSpace[palletContent] ||
                acc.latestUsedSpace[palletContent] < palletSize
              ) {
                acc.latestUsedSpace[palletContent] = palletSize;
              }
            }
          });
          Object.values(acc.latestUsedSpace).forEach(s => {
            usedSpace += s;
          });

          return set(acc, `usedSpacePerSpacebreaks.${sb._id}`, usedSpace);
        },
        { latestUsedSpace: {}, usedSpacePerSpacebreaks: {} }
      );
      return usedSpacePerSpacebreaks;
    },

    rowSpanning() {
      // Account for the Cann Group Name header at the very top of the grid
      const cannGroupHeaderCount = 1;
      // totalExpandedHeaderDepth starts at 1, each time we expand a header it increments depth by 1.
      // Each time we expand a header we add two new rows, the currentCdtName row and a row with all the direct children.
      return this.totalExpandedHeaderDepth * 2 + cannGroupHeaderCount;
    },

    selectedSpacebreakClass() {
      if (
        this.spacebreakSettings &&
        this.spacebreakSettings.open &&
        !this.spacebreakSettings.spacebreak
      ) {
        return 'selected-border-right';
      }
      return '';
    },

    sidebarHeadingStyle() {
      if (this.selectedView === this.views.Tile) {
        return { 'grid-row': `span ${this.rowSpanning}` };
      }
      // Set fixed height if unified columns is set, else set to same height as grid headers
      const height = this.unifiedColumns[this.selectedView] ? 85 : this.sidebarHeaderHeight;
      return { height: `${height}px` };
    },

    cogWarning() {
      const constraints = filter(this.selectedCanvas.constraints.definitions, c => !c.spacebreakId);
      if (constraints.length === 0) {
        return false;
      }
      const allConstraintsValuesWithinLimits = reduce(
        constraints,
        (acc, constraint) => {
          const sbConstraintsWithinLimits = values(constraint.isCurrentValueWithinLimits);
          return [...acc, ...sbConstraintsWithinLimits];
        },
        []
      );
      return !every(allConstraintsValuesWithinLimits);
    },

    globalConstraintsCount() {
      return cloneDeep(this.selectedCanvas.constraints.definitions).filter(x => !x.spacebreakId)
        .length;
    },

    stickyHeadersModel: {
      get() {
        return this.stickyHeaders;
      },
      set(value) {
        this.setStickyHeaders(value);
      },
    },

    isSticky() {
      return this.stickyHeaders ? 'sticky' : '';
    },

    unifiedColumnsModel: {
      get() {
        return this.unifiedColumns[this.selectedView];
      },
      set(value) {
        this.setUnifiedColumns({ [this.selectedView]: value });
      },
    },

    productsWithoutSpacebreak() {
      const filteredProducts = filter(
        this.getFilteredProducts.products,
        p => !p.currentSpacebreakId
      );
      return this.getProductsWithNoSpacebreak(filteredProducts);
    },

    listViewColumnsWithTitle() {
      return filter(this.listViewColumns, 'title');
    },
  },

  created() {
    this.initializeListViewColumns();
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, [
      'toggleSpaceBreakSettingsPanel',
      'setConstraintEditing',
      'setFullScreen',
      'setStickyHeaders',
      'setUnifiedColumns',
      'setActiveSpacebreak',
      'initializeListViewColumns',
      'reorderListViewColumns',
      'toggleListViewColumnVisibility',
    ]),

    toggleGlobalSettingsPanel() {
      this.setConstraintEditing({ editingBoolean: false });
      this.toggleSpaceBreakSettingsPanel(null);
    },

    toggleFullScreen(boolean) {
      this.setFullScreen(boolean);
    },

    isSpacebreakActive(spacebreak) {
      return this.activeSpacebreak === spacebreak && this.selectedView === this.views.List;
    },

    updateActiveSpacebreak(spacebreak) {
      if (this.selectedView === this.views.Tile) return;
      // Unset active spacebreak if the spacebreak is already active
      if (this.isSpacebreakActive(spacebreak)) {
        this.setActiveSpacebreak(null);
      } else {
        this.setActiveSpacebreak(spacebreak);
      }
    },

    palletSlotsPerSpacebreak(spacebreak) {
      return get(spacebreak, 'palletsSlots', 0);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.sidebar {
  min-width: 190px;
  max-width: 190px;
  min-height: 100px;
  position: relative;
  z-index: 198;
  flex-direction: column;

  &.left-column-sticky-grid-item {
    border-top: 1px solid $assortment-tile-border-colour;
    border-right: 1px solid $assortment-tile-border-colour;

    &:nth-child(2) {
      // remove the top border from the first spacebreak sidebar element to allow sticky headers to function properly
      border-top: none;
    }
  }
}

.sidebar-wrapper {
  min-width: fit-content;
  overflow-y: auto;
  overflow-x: hidden;

  .top-left-heading {
    border-right: 1px solid $assortment-tile-border-colour;
  }
}

.space-break-heading {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.spacebreak-entry {
  box-sizing: border-box;
  width: 100%;
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 7px;
}

.list-spacebreak-entry {
  padding: 7px 17px 7px 7px;
}

.active-spacebreak {
  background-color: $assortment-active-spacebreak-entry-bg-colour;
}

.spacebreak-size {
  margin-right: 5px;
}

.spacebreak-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: fit-content;
  font-size: 1.2rem;
  color: $assortment-primary-colour;
  font-weight: bold;
}

.settings-btn {
  transform: translate(2px, -4px);
  cursor: pointer;
}

.view-menu-actions {
  justify-content: flex-end;
  padding-top: 0;
}

.view-menu-switch {
  transform: scale(0.75);
}

.view-menu-option {
  font-size: 1.2rem;
}

.spacebreak-count-badge {
  z-index: 2;
  cursor: pointer;
  position: absolute;
  left: -2px;
  top: -6px;
  background-color: $assortment-primary-colour;
}

.spacebreak-count-badge-warning {
  z-index: 2;
  cursor: pointer;
  position: absolute;
  left: -2px;
  top: -6px;
  background-color: $assortment-slightly-negative-action-colour;
}

.icons-container {
  position: relative;
  padding-right: 17px;
}

.selected-border-right {
  position: relative;
  &::after {
    content: '';
    width: 3px;
    background: $assortment-primary-colour;
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
  }
}

.global-constraints {
  padding-top: 2px;
}

.fullscreen-btn {
  padding-left: 2px;
  font-size: 24px;
  color: $assortment-primary-colour;

  &::before {
    cursor: pointer;
  }
}

.sticky {
  position: sticky;
  top: 0;
}

.sticky-header-checkbox-title {
  padding-left: 7px;
}

.spacebreak-locked-border {
  box-shadow: 5px 0 0 $assortment-text-colour;
  z-index: 1;
}

.locked {
  background-color: $assortment-text-colour;
  position: absolute;
  top: 50%;
  // Needed to use px here as normal percentage positioning wouldn't work.
  right: -49px;
  transform: translateY(-50%) rotate(-90deg);
  font-size: 1.4rem;
  color: white;
  padding-right: 1.6rem;
  padding-left: 1.6rem;
  border-bottom-left-radius: 7px;
  border-bottom-right-radius: 7px;
}

.controls-container {
  .view-options-btn {
    height: 20px;
  }

  :deep(.v-btn) {
    width: 104px;
  }
}

.top-left-heading {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: 199;
  border-top: none;
  border-left: none;
  border-bottom: 1px solid $assortment-tile-border-colour;
  background: $assortment-background;
}
</style>
